/**
 * Filium wordmark. Two mark variants from assets/brand/logo_ideas_v2.png:
 *   - "graph"   → #1 Lineage Graph: an "F" built from nodes & vertices.
 *   - "layered" → #4 Layered F: bold stacked-bars F (also the favicon mark).
 * Server-safe. Includes the ™ on the wordmark.
 */
type FiliumLogoProps = {
  /** Mark height in px (wordmark scales with it). */
  size?: number;
  variant?: "graph" | "layered";
  withWordmark?: boolean;
  className?: string;
};

export function FiliumLogo({
  size = 30,
  variant = "graph",
  withWordmark = true,
  className,
}: FiliumLogoProps) {
  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap: size * 0.4 }}
    >
      {variant === "graph" ? (
        <GraphFMark size={size} />
      ) : (
        <LayeredFMark size={size} />
      )}
      {withWordmark && (
        <span
          className="wordmark"
          style={{
            fontSize: size * 0.6,
            lineHeight: 1,
            color: "var(--fg)",
            whiteSpace: "nowrap",
          }}
        >
          FILIUM
          <sup
            style={{
              fontSize: "0.4em",
              letterSpacing: 0,
              verticalAlign: "top",
              marginLeft: "0.12em",
              opacity: 0.7,
            }}
          >
            ™
          </sup>
        </span>
      )}
    </span>
  );
}

/** #1 — an "F" drawn as nodes (states) joined by edges (transformations). */
function GraphFMark({ size }: { size: number }) {
  return (
    <svg
      width={(size * 28) / 32}
      height={size}
      viewBox="0 0 28 32"
      fill="none"
      aria-hidden="true"
      style={{ display: "block", flex: "none", overflow: "visible" }}
    >
      <g stroke="url(#filium-graph-edge)" strokeWidth="1.8" strokeLinecap="round">
        {/* stem */}
        <line x1="7" y1="5" x2="7" y2="13" />
        <line x1="7" y1="13" x2="7" y2="21" />
        <line x1="7" y1="21" x2="7" y2="29" />
        {/* top arm */}
        <line x1="7" y1="5" x2="15" y2="5" />
        <line x1="15" y1="5" x2="23" y2="5" />
        {/* middle arm */}
        <line x1="7" y1="13" x2="16" y2="13" />
      </g>
      <g>
        <circle cx="7" cy="5" r="2.6" fill="#22d3ee" />
        <circle cx="7" cy="13" r="2.2" fill="#3b82f6" />
        <circle cx="7" cy="21" r="2.2" fill="#3b82f6" />
        <circle cx="7" cy="29" r="2.2" fill="#6366f1" />
        <circle cx="15" cy="5" r="2.2" fill="#38bdf8" />
        <circle cx="23" cy="5" r="2.6" fill="#22d3ee" />
        <circle cx="16" cy="13" r="2.2" fill="#38bdf8" />
      </g>
      <defs>
        <linearGradient
          id="filium-graph-edge"
          x1="0"
          y1="0"
          x2="28"
          y2="32"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#22d3ee" />
          <stop offset="1" stopColor="#6366f1" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** #4 — three horizontal lines (layered intermediate: SOURCE → IR → TARGET). */
function LayeredFMark({ size }: { size: number }) {
  return (
    <svg
      width={(size * 28) / 32}
      height={size}
      viewBox="0 0 28 32"
      fill="none"
      aria-hidden="true"
      style={{ display: "block", flex: "none" }}
    >
      <g fill="url(#filium-layered)">
        <rect x="3" y="6" width="22" height="5.2" rx="2.2" />
        <rect x="3" y="13.4" width="15" height="5.2" rx="2.2" />
        <rect x="3" y="20.8" width="10" height="5.2" rx="2.2" />
      </g>
      <defs>
        <linearGradient
          id="filium-layered"
          x1="3"
          y1="6"
          x2="20"
          y2="26"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#22d3ee" />
          <stop offset="0.55" stopColor="#3b82f6" />
          <stop offset="1" stopColor="#6366f1" />
        </linearGradient>
      </defs>
    </svg>
  );
}
